// Person assignment (portal-owned) — SAP orders know work centers, not people. Supervisors
// (canApprove) assign; everyone sees who owns the order; "My orders" merges assignments.
//
// The input is a combobox: on focus it offers the recently used assignees, typing filters the
// full PM user list. Free text stays allowed (contractors without portal accounts) — picking
// from the list just prevents the silent-typo case where an assignment never reaches anyone's
// "My orders".
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { UserCheck } from 'lucide-react';
import { useTheme } from '../../../../core/ui/ThemeContext';
import { useLanguage } from '../../../../core/i18n/LanguageContext';
import { Card, Button } from '../ui';
import { setOrderAssignee, getAssigneeSuggest } from '../../api';

const MAX_OPTIONS = 10;

export default function AssigneePanel({ user, order, role, canAssign, onChanged }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [name, setName] = useState(order.assignee ? order.assignee.username : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [suggest, setSuggest] = useState(null); // { recent: [], users: [] } after first focus
  const boxRef = useRef(null);
  const sub = isDarkMode ? 'text-white/50' : 'text-slate-500';

  // Lazy: fetch once on first focus, not on every order screen (supervisors only reach this
  // when they actually open the field). A failed fetch degrades to the old free-text input.
  const loadSuggest = () => {
    if (suggest !== null) return;
    getAssigneeSuggest(user, role)
      .then((res) => setSuggest(res && Array.isArray(res.users) ? res : { recent: [], users: [] }))
      .catch(() => setSuggest({ recent: [], users: [] }));
  };

  // Close on click/tap outside (mousedown so option onMouseDown still wins the race).
  useEffect(() => {
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const save = async (username) => {
    setBusy(true);
    setError(null);
    setOpen(false);
    try {
      const res = await setOrderAssignee(user, order.orderId, username, role);
      if (res && res.ok === false) setError(res.error);
      else if (onChanged) onChanged();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  // Empty input -> recent assignees; typed input -> filter the full user list (name or display
  // name, case-insensitive). Computed per keystroke over at most a few hundred users — cheap.
  const options = useMemo(() => {
    if (!suggest) return [];
    const q = name.trim().toLowerCase();
    if (!q) {
      return suggest.recent.slice(0, MAX_OPTIONS).map((r) => ({ username: r.username, label: r.username }));
    }
    return suggest.users
      .filter((u) => u.username.toLowerCase().includes(q)
        || (u.displayName && u.displayName.toLowerCase().includes(q)))
      .slice(0, MAX_OPTIONS)
      .map((u) => ({
        username: u.username,
        label: u.displayName && u.displayName !== u.username ? `${u.username} — ${u.displayName}` : u.username,
      }));
  }, [suggest, name]);

  // Free-text hint only when the typed name matches NO portal user exactly (typo guard).
  const isFreeText = useMemo(() => {
    if (!suggest || !name.trim()) return false;
    const q = name.trim().toLowerCase();
    return !suggest.users.some((u) => u.username.toLowerCase() === q);
  }, [suggest, name]);

  if (!canAssign && !order.assignee) return null;

  return (
    <Card className="mb-3">
      <div className="flex items-center gap-2 text-sm">
        <UserCheck size={18} className={sub} />
        <span className={sub}>{t('pm.assign.label')}:</span>
        <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
          {order.assignee ? order.assignee.username : t('pm.assign.nobody')}
        </span>
      </div>
      {canAssign && (
        <div className="flex gap-2 mt-2">
          <div className="relative flex-1" ref={boxRef}>
            <input value={name}
              onChange={(e) => { setName(e.target.value); setOpen(true); }}
              onFocus={() => { loadSuggest(); setOpen(true); }}
              placeholder={t('pm.assign.placeholder')}
              className={`w-full rounded-xl px-3 py-2 text-sm ${isDarkMode ? 'bg-white/10 text-white placeholder-white/40' : 'bg-slate-100 text-slate-800 placeholder-slate-400'}`} />
            {open && suggest && (options.length > 0 || isFreeText) && (
              <div className={`absolute left-0 right-0 top-full mt-1 z-20 rounded-xl border shadow-lg overflow-hidden ${isDarkMode
                ? 'bg-gray-900 border-white/10' : 'bg-white border-slate-200'}`}>
                {!name.trim() && options.length > 0 && (
                  <div className={`px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide ${sub}`}>
                    {t('pm.assign.recent')}
                  </div>
                )}
                {options.map((o) => (
                  <button key={o.username} type="button" disabled={busy}
                    /* onMouseDown, not onClick: fires before the input's blur/outside handling */
                    onMouseDown={(e) => { e.preventDefault(); setName(o.username); save(o.username); }}
                    className={`block w-full text-left px-3 py-2 text-sm ${isDarkMode
                      ? 'text-white hover:bg-white/10' : 'text-slate-800 hover:bg-slate-100'}`}>
                    {o.label}
                  </button>
                ))}
                {isFreeText && (
                  <div className={`px-3 py-2 text-xs ${sub}`}>{t('pm.assign.freeText')}</div>
                )}
              </div>
            )}
          </div>
          <Button variant="secondary" className="!min-h-[40px] !w-auto px-3 !text-sm" disabled={busy}
            onClick={() => save(name)}>{t('pm.assign.set')}</Button>
          <Button variant="secondary" className="!min-h-[40px] !w-auto px-3 !text-sm" disabled={busy}
            onClick={() => { setName(user?.username || ''); save(user?.username || ''); }}>
            {t('pm.assign.toMe')}
          </Button>
        </div>
      )}
      {error && <p className={`text-sm mt-1 ${isDarkMode ? 'text-rose-300' : 'text-rose-600'}`}>{error}</p>}
    </Card>
  );
}
